import { useEffect, useState } from "react";
import { useEditorStore } from "@/store";

export interface SpriteEntry {
  name: string;
  x: number;
  y: number;
  width: number;
  height: number;
  pixelRatio: number;
  sdf: boolean;
}

const cache = new Map<string, SpriteEntry[]>();

/**
 * Fetches and caches the sprite.json for the current style's sprite URL.
 * Returns a list of available image names with their metadata.
 */
export function useSpriteImages() {
  const spriteRaw = useEditorStore((s) => s.style.sprite);
  const [images, setImages] = useState<SpriteEntry[]>([]);
  const [loading, setLoading] = useState(false);

  // Resolve the sprite URL — can be string or array of {id, url}
  const spriteUrl = typeof spriteRaw === "string"
    ? spriteRaw
    : Array.isArray(spriteRaw) && spriteRaw.length > 0
      ? (spriteRaw[0] as { url?: string })?.url
      : undefined;

  useEffect(() => {
    if (!spriteUrl) {
      setImages([]);
      return;
    }

    const jsonUrl = `${spriteUrl}.json`;

    const cached = cache.get(jsonUrl);
    if (cached) {
      setImages(cached);
      return;
    }

    let cancelled = false;
    setLoading(true);

    fetch(jsonUrl)
      .then((r) => r.json())
      .then((json: Record<string, { x: number; y: number; width: number; height: number; pixelRatio?: number; sdf?: boolean }>) => {
        if (cancelled) return;
        const entries: SpriteEntry[] = Object.entries(json)
          .map(([name, data]) => ({
            name,
            x: data.x,
            y: data.y,
            width: data.width,
            height: data.height,
            pixelRatio: data.pixelRatio ?? 1,
            sdf: data.sdf ?? false,
          }))
          .sort((a, b) => a.name.localeCompare(b.name));
        cache.set(jsonUrl, entries);
        setImages(entries);
      })
      .catch(() => {
        if (!cancelled) setImages([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [spriteUrl]);

  return { images, loading, spriteUrl };
}
